import { ChangeEvent, ReactElement, useCallback, useMemo, useState } from 'react'
import { useIsOrganisationAdmin } from '@hooks'
import chunk from 'lodash-es/chunk'
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TablePagination,
  Stack,
} from '@mui/material'
import type { ProjectConnectionUsageDTO } from '@api/fastAPI.schemas'
import { UseMutationResult } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'
import { connectionSchema } from '../ConnectionForm/schema'
import ConnectionListItem from '../ConnectionListItem'
import CreateConnectionDialog from '../CreateConnectionDialog'
import EditConnectionDialog from '../EditConnectionDialog'
import PasteConnectionFromClipboardButton from '../PasteConnectionFromClipboardButton'
import {
  preparePastedConnection,
  serializeConnectionForClipboard,
} from '../utils/clipboardConnection'
import {
  TENSILE_SHORT_NAME_PREFIX,
  getConnectionShortNameForCopy,
  getDefaultConnectionShortName,
} from '../utils/shortNames'

interface Props {
  connections: Array<Connection | ProjectConnection>
  onDelete: (connection: Connection | ProjectConnection) => void
  createMutation:
    | UseMutationResult<Connection, unknown, Connection, unknown>
    | UseMutationResult<ProjectConnection, unknown, ProjectConnection, unknown>
  editMutation:
    | UseMutationResult<Connection, unknown, Connection, unknown>
    | UseMutationResult<ProjectConnection, unknown, ProjectConnection, unknown>
  defaultRowsPerPage?: 5 | 10 | 25
  isProjectLibrary?: boolean
  /** Project library: how many anchors use each Zugverbinder, by connection guid. */
  usageByGuid?: Record<string, ProjectConnectionUsageDTO>
}

const ConnectionList = ({
  connections,
  onDelete,
  createMutation,
  editMutation,
  defaultRowsPerPage = 25,
  isProjectLibrary = false,
  usageByGuid,
}: Props): ReactElement => {
  // The library belongs to the organisation: every member sees it, only an
  // admin of that organisation may change it. Assume read-only until we know
  // otherwise, so no control is offered that the backend would refuse.
  const { isOrganisationAdmin } = useIsOrganisationAdmin()
  const { enqueueSnackbar } = useSnackbar()
  const readOnly = !isProjectLibrary && !isOrganisationAdmin
  const [createValues, setCreateValues] = useState<Connection | null>(null)
  const [editValues, setEditValues] = useState<Connection | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(defaultRowsPerPage)

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleCopy = useCallback(
    (connection: Connection | ProjectConnection) => {
      try {
        navigator.clipboard.writeText(serializeConnectionForClipboard(connection))
        enqueueSnackbar('Zugverbinder in Zwischenablage kopiert', { variant: 'success' })
      } catch {
        enqueueSnackbar('Fehler beim Kopieren des Zugverbinders', { variant: 'error' })
      }
    },
    [enqueueSnackbar],
  )

  // A copy keeps its short name only while no connector here uses it yet.
  const handlePaste = useCallback(
    (connection: Connection) => {
      setCreateValues({
        ...connection,
        short_name: getConnectionShortNameForCopy(
          connection,
          connections,
          TENSILE_SHORT_NAME_PREFIX,
        ),
      })
    },
    [connections],
  )

  // A duplicate is the copy a paste produces -- new guid, "(Kopie)" name -- opened
  // in the create dialog so it can be renamed before it is saved.
  const handleDuplicate = useCallback(
    (connection: Connection | ProjectConnection) => {
      handlePaste(preparePastedConnection(connection))
    },
    [handlePaste],
  )

  const allShortNames = useMemo(
    () => connections.map(connection => connection.short_name),
    [connections],
  )
  // While editing, the connector's own short name is not taken.
  const shortNamesTakenByOthers = useMemo(
    () =>
      connections
        .filter(connection => connection.guid !== editValues?.guid)
        .map(connection => connection.short_name),
    [connections, editValues],
  )

  const SelectButton = (
    <Button
      onClick={() =>
        setCreateValues({
          ...connectionSchema.getDefault(),
          short_name: getDefaultConnectionShortName(connections, TENSILE_SHORT_NAME_PREFIX),
        })
      }
      variant="outlined"
      color="primary"
      size="small"
      sx={{
        alignSelf: 'center',
      }}
      data-cy="create-connection"
    >
      anlegen
    </Button>
  )

  const CreateActions = !readOnly && (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
      <PasteConnectionFromClipboardButton onPaste={handlePaste} />
      {SelectButton}
    </Stack>
  )

  const chunks = useMemo(() => chunk(connections, rowsPerPage), [connections, rowsPerPage])
  const connectionsInPage = chunks[page] || chunks[page - 1] || []

  const hasConnections = !!connections.length

  return (
    <>
      <Box>
        <Box display="flex" mb={1} justifyContent="space-between">
          <Typography variant="h6" mb={1}>
            Zugverbinder {`(${connections.length})`}
          </Typography>
          {hasConnections && CreateActions}
        </Box>
        {!hasConnections && (
          <Paper>
            <Box padding={3} textAlign="center">
              <Typography>
                {readOnly
                  ? 'Es sind noch keine Zugverbindungen angelegt'
                  : 'Sie haben noch keine Zugverbindungen angelegt'}
              </Typography>
              {CreateActions && <Box mt={2}>{CreateActions}</Box>}
            </Box>
          </Paper>
        )}
        {hasConnections && (
          <>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Kurzbezeichnung</TableCell>
                    <TableCell>Kommentar im Export</TableCell>
                    <TableCell>
                      <>
                        R<sub>d</sub> Erdbeben (kN)
                      </>
                    </TableCell>
                    <TableCell>
                      <>
                        R<sub>d</sub> Wind (kN)
                      </>
                    </TableCell>
                    {usageByGuid && <TableCell>Verwendet (davon manuell)</TableCell>}
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {connectionsInPage.map(connection => {
                    return (
                      <ConnectionListItem
                        key={connection.guid}
                        connection={connection}
                        isProjectLibrary={isProjectLibrary}
                        usage={usageByGuid?.[connection.guid]}
                        showUsage={!!usageByGuid}
                        onDelete={() => onDelete(connection)}
                        onEdit={() => setEditValues(connection)}
                        onCopy={() => handleCopy(connection)}
                        onDuplicate={() => handleDuplicate(connection)}
                      />
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Zugverbindungen pro Seite"
              labelDisplayedRows={({ from, to, count }) => {
                return `${from}–${to} von ${count !== -1 ? count : `mehr als ${to}`}`
              }}
              component="div"
              count={connections.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Box>
      {createValues && (
        <CreateConnectionDialog
          mutation={createMutation}
          open
          onClose={() => setCreateValues(null)}
          connection={createValues}
          takenShortNames={allShortNames}
        />
      )}
      {editValues && (
        <EditConnectionDialog
          mutation={editMutation}
          open
          onClose={() => setEditValues(null)}
          connection={editValues}
          takenShortNames={shortNamesTakenByOthers}
        />
      )}
    </>
  )
}

export default ConnectionList
